/**
 * First-party marketing hosts on *.smartmetrics.com.
 * These must never resolve as agency / portal tenants.
 */
export const MARKETING_SUBDOMAINS = new Set(['builders'])

export const MARKETING_ROOT_PATHS: Record<string, string> = {
  builders: '/services/home-builders',
}

export const BUILDERS_PRODUCTION_ORIGIN = 'https://builders.smartmetrics.com'
export const CANONICAL_PRODUCTION_ORIGIN = 'https://smartmetrics.com'

/**
 * GTM (and CookieYes inside it) is registered to smartmetrics.com. Preview
 * deployments live on *.vercel.app, which CookieYes treats as a domain change
 * and then throws into the error inbox.
 *
 * `vercelEnv` is process.env.VERCEL_ENV when called from a server component.
 * Preview/dev deployments must never load production tags, even if the Host
 * header looks like a production domain.
 */
export function shouldLoadProductionTags(
  hostname: string,
  vercelEnv: string | undefined = process.env.VERCEL_ENV
): boolean {
  if (vercelEnv && vercelEnv !== 'production') return false

  const host = hostname.toLowerCase().split(':')[0]
  if (!host) return false
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return false
  if (host.endsWith('.localhost')) return false
  if (host.endsWith('.vercel.app')) return false
  if (host.endsWith('.now.sh')) return false
  return true
}

/** Apex origin for chrome links when the visitor is on a marketing subdomain. */
export function marketingSiteOriginFromHost(hostname: string, port?: string): string {
  const host = hostname.toLowerCase()
  const marketingSub = host.endsWith('.smartmetrics.com')
    ? host.slice(0, -'.smartmetrics.com'.length)
    : host.endsWith('.localhost')
      ? host.slice(0, -'.localhost'.length)
      : ''
  if (!isMarketingSubdomain(marketingSub)) return ''
  if (host.endsWith('.localhost') || host === 'localhost') {
    return `http://localhost${port ? `:${port}` : ''}`
  }
  return CANONICAL_PRODUCTION_ORIGIN
}

export function siteHref(path: string, origin: string): string {
  if (!origin) return path
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

export function isMarketingSubdomain(sub: string | null | undefined): boolean {
  if (!sub) return false
  return MARKETING_SUBDOMAINS.has(sub.toLowerCase())
}

/** Subdomain label for a marketing host, or '' on the apex / unrelated hosts. */
export function marketingSubdomainFromHost(hostname: string): string {
  const host = hostname.toLowerCase().split(':')[0]
  if (host.endsWith('.smartmetrics.com')) {
    return host.slice(0, -'.smartmetrics.com'.length)
  }
  if (host.endsWith('.localhost')) {
    return host.slice(0, -'.localhost'.length)
  }
  return ''
}

/** Origin Stripe should send playbook buyers back to. */
export function playbookCheckoutOrigin(req: { headers: Headers; nextUrl: URL }): string {
  const hostHeader = (req.headers.get('x-forwarded-host') || req.headers.get('host') || '').split(',')[0].trim()
  const host = hostHeader.toLowerCase().split(':')[0]
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.localhost')
  if (isLocal) {
    const proto = req.nextUrl.protocol.replace(':', '') || 'http'
    return `${proto}://${hostHeader}`
  }
  if (host.endsWith('.vercel.app')) {
    return `https://${hostHeader}`
  }
  return BUILDERS_PRODUCTION_ORIGIN
}
